import { Suspense, Component, ReactNode } from 'react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { AlertCircle } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from "@/components/ui/spinner";
import { InstrumentedErrorBoundaryGenericDisplay } from "@/components/error-boundary";

/**
 * Error boundary for catching and displaying step errors gracefully
 */
class StepErrorBoundary extends Component<
  { children: ReactNode; onReset?: () => void },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: ReactNode; onReset?: () => void }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error('Step error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription className="mt-2">
            <p className="mb-4">
              {this.state.error?.message || 'Failed to load this step. Please try again.'}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  this.setState({ hasError: false, error: undefined })
                  this.props.onReset?.()
                }}
              >
                Try Again
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.reload()}
              >
                Reload Page
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )
    }

    return this.props.children
  }
}

/**
 * Individual skeleton loaders for each section
 */
function HeaderSkeleton() {
  return (
    <div className="space-y-2">
      <Skeleton className="h-8 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
    </div>
  )
}

function MainSkeleton() {
  return (
    <div className="w-full h-full flex items-center justify-center">
      <Spinner/>
    </div>
  )
}

function FooterSkeleton() {
  return (
    <div className="flex gap-2">
      <Skeleton className="h-10 w-24" />
      <Skeleton className="h-10 w-24" />
    </div>
  )
}

/**
 * StepFrame - Layout component for install flow steps
 *
 * Provides consistent structure with three outlets:
 * - Optional sticky header at top
 * - Required scrollable main content area
 * - Optional sticky footer at bottom
 *
 * Includes built-in error boundary and suspense for loading states.
 * All steps should use this component to ensure consistent layout,
 * spacing, sticky header/footer behavior, and error handling.
 */

interface StepFrameProps {
  /**
   * Optional header content - typically a StepHeader component
   * Will be rendered in a sticky container at the top
   */
  header?: ReactNode

  /**
   * Required main content area
   * This section will be scrollable if content exceeds viewport
   */
  main: ReactNode

  /**
   * Optional footer content - typically a StepFooter component
   * Will be rendered in a sticky container at the bottom
   */
  footer?: ReactNode
}

export function StepFrame({ header, main, footer }: StepFrameProps) {
  return (
    <StepErrorBoundary>
      <div className="flex flex-col min-h-[calc(100vh-var(--nav-height))]">
        {/* Sticky Header - Full width, positioned below main nav using CSS custom property */}
        {header && (
          <div className="sticky top-[var(--nav-height,73px)] z-40 bg-background border-b px-2 lg:px-8 py-4">
            <Suspense fallback={<HeaderSkeleton />}>
              {header}
            </Suspense>
          </div>
        )}

        {/* Scrollable Main Content - With horizontal padding */}
        <div className="flex-1 px-4 lg:px-8 py-4" id="main-install-flow-step-content">
          <InstrumentedErrorBoundaryGenericDisplay>
            <Suspense fallback={<MainSkeleton />}>
              {main}
            </Suspense>
          </InstrumentedErrorBoundaryGenericDisplay>
        </div>

        {/* Sticky Footer - Full width */}
        {footer && (
          <div className="sticky bottom-0 z-40 bg-background border-t px-4 lg:px-8 py-4">
            <Suspense fallback={<FooterSkeleton />}>
              {footer}
            </Suspense>
          </div>
        )}
      </div>
    </StepErrorBoundary>
  )
}
